import { Controller } from "@hotwired/stimulus"

// Auto-rotates through dashboard pages at a set interval
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 30000 }, // Default 30 seconds
    nextUrl: String,
    enabled: { type: Boolean, default: false },
    countdown: { type: Number, default: 30 }
  }
  
  static targets = ["toggleButton", "countdown", "intervalSelector"]
  
  connect() {
    // Check if auto-rotation was previously enabled (using localStorage)
    const savedState = localStorage.getItem('dashboardAutoRotate')
    const savedInterval = localStorage.getItem('dashboardAutoRotateInterval')
    
    if (savedInterval) {
      this.intervalValue = parseInt(savedInterval)
      this.countdownValue = this.intervalValue / 1000
      
      if (this.hasIntervalSelectorTarget) {
        this.intervalSelectorTarget.value = this.intervalValue
      }
    }
    
    // Always default to disabled unless explicitly enabled before
    if (savedState === 'true') {
      this.enabledValue = true
      this.updateButtonState()
      this.startRotation()
    } else {
      this.enabledValue = false
      localStorage.setItem('dashboardAutoRotate', 'false')
      this.updateButtonState()
    }
  }
  
  disconnect() {
    this.stopRotation()
  }
  
  toggle() {
    this.enabledValue = !this.enabledValue
    localStorage.setItem('dashboardAutoRotate', this.enabledValue)
    
    if (this.enabledValue) {
      this.startRotation()
    } else {
      this.stopRotation()
    }
    
    this.updateButtonState()
  }
  
  changeInterval(event) {
    const newInterval = parseInt(event.target.value)
    this.intervalValue = newInterval
    this.countdownValue = newInterval / 1000
    
    localStorage.setItem('dashboardAutoRotateInterval', newInterval)
    
    // Restart timer if it's currently running
    if (this.enabledValue) {
      this.stopRotation()
      this.startRotation()
    }
    
    this.updateButtonState()
  }
  
  startRotation() {
    // Reset countdown to full interval
    this.countdownValue = this.intervalValue / 1000
    this.updateCountdown()
    
    // Start countdown timer (every second)
    this.countdownTimer = setInterval(() => {
      this.countdownValue = Math.max(0, this.countdownValue - 1)
      this.updateCountdown()
      
      // When countdown reaches zero, reset it
      if (this.countdownValue <= 0) {
        this.countdownValue = this.intervalValue / 1000
      }
    }, 1000)
    
    // Start rotation timer
    this.rotationTimer = setInterval(() => {
      if (this.nextUrlValue) {
        // Reset countdown when navigating
        this.countdownValue = this.intervalValue / 1000
        Turbo.visit(this.nextUrlValue)
      }
    }, this.intervalValue)
  }
  
  stopRotation() {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer)
      this.rotationTimer = null
    }
    
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  }
  
  updateCountdown() {
    if (this.hasCountdownTarget) {
      if (this.enabledValue) {
        this.countdownTarget.textContent = this.countdownValue
        this.countdownTarget.classList.remove('hidden')
      } else {
        this.countdownTarget.classList.add('hidden')
      }
    }
  }
  
  updateButtonState() {
    if (this.hasToggleButtonTarget) {
      if (this.enabledValue) {
        this.toggleButtonTarget.classList.add('bg-green-50', 'dark:bg-green-900/30', 'text-green-700', 'dark:text-green-400', 'ring-green-700/10', 'dark:ring-green-400/30')
        this.toggleButtonTarget.classList.remove('bg-zinc-50', 'dark:bg-zinc-700/30', 'text-zinc-700', 'dark:text-zinc-400', 'ring-zinc-700/10', 'dark:ring-zinc-400/30')
        
        const intervalInSeconds = this.intervalValue / 1000;
        const intervalText = intervalInSeconds >= 60 ? 
          `${intervalInSeconds / 60} min` : 
          `${intervalInSeconds} sec`;
          
        this.toggleButtonTarget.innerHTML = `
          <i class="ph ph-play mr-1"></i> 
          Auto: <span data-auto-rotate-target="countdown" class="font-mono ml-1">${this.countdownValue}</span>s
        `;
      } else {
        this.toggleButtonTarget.classList.remove('bg-green-50', 'dark:bg-green-900/30', 'text-green-700', 'dark:text-green-400', 'ring-green-700/10', 'dark:ring-green-400/30')
        this.toggleButtonTarget.classList.add('bg-zinc-50', 'dark:bg-zinc-700/30', 'text-zinc-700', 'dark:text-zinc-400', 'ring-zinc-700/10', 'dark:ring-zinc-400/30')
        this.toggleButtonTarget.innerHTML = '<i class="ph ph-pause mr-1"></i> Auto: Off';
      }
    }
  }
}
